import { ArrowLeft, Beaker, Info } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { StudyShell } from './StudyShell';
import { toolsHubPath } from './studyTools';
import {
  VOLUMETRIC_CONDITIONS,
  VOLUMETRIC_MODEL_BOUNDARIES,
  VOLUMETRIC_MODES,
  VOLUMETRIC_SOURCES,
  type VolumetricMode,
} from './volumetricAnalysis';
import { VolumetricAnalysisExplorePanel } from './VolumetricAnalysisExplorePanel';
import { VolumetricAnalysisStandard } from './VolumetricAnalysisStandard';
import { VolumetricAnalysisTitration } from './VolumetricAnalysisTitration';

/** Explore comes first; the reviewed problem sets stay as Practice tabs. */
type PageTab = 'explore' | VolumetricMode;
const PAGE_TABS: readonly PageTab[] = ['explore', ...VOLUMETRIC_MODES];

const MODE_COPY: Record<
  PageTab,
  { tab: string; eyebrow: string; title: string; description: string }
> = {
  explore: {
    tab: 'Explore',
    eyebrow: 'Your own numbers',
    title: 'Mass → standard → titres → unknown',
    description:
      'Type the mass of a primary standard, the aliquot and your titre readings. The working, titre chart, mole map and titration bench update together.',
  },
  standard: {
    tab: 'Standard solutions',
    eyebrow: 'Primary standards',
    title: 'Start from something you can trust',
    description:
      'Decide which substances can be weighed straight into a standard solution, then calculate masses, concentrations and dilutions.',
  },
  'acid-base': {
    tab: 'Acid–base titrations',
    eyebrow: 'Direct titration',
    title: 'Titre → moles → mole ratio → answer',
    description:
      'Choose the concordant titres, average them, then use the balanced equation to reach the unknown.',
  },
  back: {
    tab: 'Back titrations',
    eyebrow: 'Excess, then leftover',
    title: 'Add too much, then measure what is left',
    description:
      'For solids that react slowly or do not dissolve, add a known excess and titrate the leftover. Subtract to find what reacted.',
  },
  redox: {
    tab: 'Redox titrations',
    eyebrow: 'Permanganate, dichromate, iodine',
    title: 'Electrons set the mole ratio',
    description:
      'Use balanced redox equations, including self-indicating permanganate and iodine with thiosulfate.',
  },
};

export function VolumetricAnalysisLabPage() {
  const [mode, setMode] = useState<PageTab>('explore');
  const copy = MODE_COPY[mode];

  return (
    <StudyShell scienceTutor={{ subjectKey: 'chemistry', toolId: 'volumetric-analysis', state: { mode } }}>
      <Link
        to={toolsHubPath('chemistry')}
        className="inline-flex items-center gap-2 text-sm font-extrabold text-slate2 hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Chemistry tools
      </Link>

      <header className="chemistry-tool-header mt-5 flex flex-col gap-5 rounded-[2rem] border border-brand-sky/25 bg-gradient-to-br from-wash-sky via-canvas-pure to-wash-sunshine p-6 shadow-card-soft lg:flex-row lg:items-end lg:justify-between lg:p-8">
        <div className="max-w-3xl">
          <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.16em] text-brand-sky">
            <Beaker className="h-4 w-4" aria-hidden="true" /> Acids, ions &amp; electrochemistry
          </p>
          <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
            Volumetric Analysis
          </h1>
          <p className="mt-3 max-w-2xl font-medium leading-7 text-slate2">
            Work like an analyst: pick concordant titres, follow the moles through the balanced
            equation and find the unknown concentration or purity.
          </p>
        </div>
        <div className="rounded-2xl border border-white/80 bg-white/75 px-4 py-3 text-sm font-bold text-ink-soft">
          Explore tool · no mastery claim
        </div>
      </header>

      <div
        className="mt-6 flex max-w-full flex-wrap gap-1 rounded-[1.5rem] border border-hairline bg-canvas-pure p-1 shadow-card-soft sm:inline-flex sm:rounded-full"
        role="tablist"
        aria-label="Volumetric analysis topic"
      >
        {PAGE_TABS.map((candidate) => (
          <button
            key={candidate}
            type="button"
            role="tab"
            aria-selected={mode === candidate}
            onClick={() => setMode(candidate)}
            className={`rounded-full px-4 py-2.5 text-sm font-extrabold ${mode === candidate ? 'bg-ink text-white' : 'text-slate2'}`}
          >
            {MODE_COPY[candidate].tab}
          </button>
        ))}
      </div>

      <div className="mt-6">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
          {copy.eyebrow}
        </p>
        <h2 className="mt-1 text-2xl font-extrabold text-ink">{copy.title}</h2>
        <p className="mt-1 font-medium text-slate2">{copy.description}</p>
        <p className="mt-2 text-sm font-bold text-ink-soft">{VOLUMETRIC_CONDITIONS}</p>
      </div>

      <div key={mode} className="mt-5">
        {mode === 'explore' ? (
          <VolumetricAnalysisExplorePanel />
        ) : mode === 'standard' ? (
          <VolumetricAnalysisStandard />
        ) : (
          <VolumetricAnalysisTitration mode={mode} />
        )}
      </div>

      <section className="mt-6 rounded-[1.5rem] border border-hairline bg-surface p-5 text-sm font-medium leading-6 text-slate2">
        <p className="flex items-center gap-2 font-extrabold text-ink">
          <Info className="h-4 w-4" aria-hidden="true" /> Model boundaries and sources
        </p>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          {VOLUMETRIC_MODEL_BOUNDARIES.map((line) => (
            <li key={line}>{line}</li>
          ))}
          <li>Progress is not saved. Values are pending teaching review.</li>
        </ul>
        <p className="mt-3">
          Sources:{' '}
          {VOLUMETRIC_SOURCES.map((source) => `${source.label} — ${source.detail}`).join(' ')}
        </p>
      </section>
    </StudyShell>
  );
}
